/**
 * SegTabs — переключатель-таблетка с ползущим индикатором (стиль терминала,
 * 15.10). Индикатор едет под выбранную кнопку: позицию меряем по DOM, потому
 * что подписи разной ширины и меняются с языком.
 */
import React, { useLayoutEffect, useRef, useState } from 'react';
import clsx from 'clsx';

export interface SegItem<K extends string> {
  key: K;
  label: React.ReactNode;
  badge?: React.ReactNode;
  title?: string;
}

interface Props<K extends string> {
  items: Array<SegItem<K>>;
  value: K | null;
  onChange: (k: K) => void;
  /** Цветной индикатор: оранжевый (главный выбор) или красный (закрытие). */
  accent?: boolean | 'danger';
  className?: string;
  btnClassName?: string;
  /** Растянуть кнопки на всю ширину поровну. */
  stretch?: boolean;
}

export function SegTabs<K extends string>({ items, value, onChange, accent, className, btnClassName, stretch }: Props<K>) {
  const boxRef = useRef<HTMLDivElement>(null);
  const btnRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const [thumb, setThumb] = useState<{ left: number; width: number } | null>(null);

  useLayoutEffect(() => {
    const box = boxRef.current;
    if (!box) return;
    const measure = () => {
      const b = value != null ? btnRefs.current[value] : null;
      if (!b) { setThumb(null); return; }
      setThumb({ left: b.offsetLeft, width: b.offsetWidth });
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(box);
    return () => ro.disconnect();
  }, [value, items.length]);

  return (
    <div ref={boxRef} className={clsx('sb-seg', stretch && 'flex w-full', className)}>
      {thumb && (
        <span className={clsx('sb-seg-thumb', accent === 'danger' ? 'sb-accent sb-danger' : accent && 'sb-accent')}
              style={{ left: thumb.left, width: thumb.width }} />
      )}
      {items.map(it => (
        <button
          key={it.key}
          ref={el => { btnRefs.current[it.key] = el; }}
          type="button"
          title={it.title}
          data-on={value === it.key}
          onClick={() => onChange(it.key)}
          className={clsx('sb-seg-btn', stretch && 'flex-1 justify-center', btnClassName)}
        >
          {it.label}
          {it.badge}
        </button>
      ))}
    </div>
  );
}

/** Значение, которое коротко вспыхивает зелёным/красным при изменении. */
export const FlashValue: React.FC<{ value: number | null; children: React.ReactNode; className?: string }> = ({ value, children, className }) => {
  const prev = useRef<number | null>(value);
  const [tick, setTick] = useState<{ n: number; dir: 'up' | 'down' | null }>({ n: 0, dir: null });
  useLayoutEffect(() => {
    const p = prev.current;
    prev.current = value;
    if (p == null || value == null || p === value) return;
    setTick(t => ({ n: t.n + 1, dir: value > p ? 'up' : 'down' }));
  }, [value]);
  return (
    <span key={tick.n} className={clsx(className, tick.dir === 'up' && 'sb-flash-up', tick.dir === 'down' && 'sb-flash-down')}>
      {children}
    </span>
  );
};
